// /team/profiles — perfiles de acceso del backoffice.
//
// Un perfil es un nombre y una matriz: por cada seccion del backoffice, si la
// cuenta no la ve, la ve, o la ve y opera. Las cuentas (Equipo → Cuentas)
// apuntan a un perfil. Desde aca se crean perfiles nuevos y se ajustan los que
// existen — sin deploy.
//
// Salvaguardas (el backend las repite):
//   - `admin` es inmutable en su acceso. Solo cambia de nombre.
//   - No se puede quitarle Equipo (nivel completo) al propio perfil.
//   - Un perfil con cuentas no se borra: primero se reasignan.
//
// Guardar un cambio de acceso cierra las sesiones de todas las cuentas con
// ese perfil, incluida la propia si aplica: la sesion lleva el mapa copiado
// al login, y es la unica forma de que el cambio valga ya.

import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Users } from 'lucide-react';
import {
  listRoles,
  createRole,
  patchRole,
  deleteRole,
  AdminApiError,
  type AdminRoleItem,
} from '@/lib/api';
import {
  ACCESS_SECTIONS,
  SECTION_META,
  canWrite,
  type AccessLevel,
  type AccessMap,
  type AccessSection,
} from '@/lib/access';
import { useSession } from '@/hooks/useSession';
import { fieldCls } from '@/lib/form-styles';
import TeamTabs from '@/components/TeamTabs';

type Modal =
  | { kind: 'create' }
  | { kind: 'edit'; target: AdminRoleItem }
  | { kind: 'delete'; target: AdminRoleItem }
  | null;

const LEVEL_LABEL: Record<AccessLevel, string> = {
  hidden: 'Oculto',
  read: 'Solo lectura',
  write: 'Completo',
};

function levelBadgeCls(level: AccessLevel): string {
  switch (level) {
    case 'write':
      return 'bg-brand/20 text-brand';
    case 'read':
      return 'bg-status-info/20 text-status-info';
    default:
      return 'bg-ink-muted/15 text-ink-muted';
  }
}

function errorMessage(e: unknown, fallback: string): string {
  if (!(e instanceof AdminApiError)) return fallback;
  switch (e.code) {
    case 'role_protected':
      return e.message.includes('own role')
        ? 'No podés quitarle la gestión del equipo a tu propio perfil: te quedarías afuera de esta pantalla.'
        : 'El perfil admin no se puede modificar ni borrar.';
    case 'role_in_use':
      return 'Hay cuentas que todavía usan este perfil. Reasignalas primero desde Cuentas.';
    case 'role_already_exists':
      return 'Ya existe un perfil con esa clave.';
    case 'role_not_found':
      return 'Ese perfil ya no existe.';
    default:
      return e.message || fallback;
  }
}

export default function AccessProfiles(): JSX.Element {
  const session = useSession();
  const qc = useQueryClient();
  const [modal, setModal] = useState<Modal>(null);
  const canEdit = canWrite(session, 'team');

  const q = useQuery({ queryKey: ['admin-roles'], queryFn: listRoles });

  function refresh(): void {
    qc.invalidateQueries({ queryKey: ['admin-roles'] });
    qc.invalidateQueries({ queryKey: ['admin-admins'] });
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 data-help-page="profiles.page" data-help="profiles.page" className="text-2xl font-semibold">
            Perfiles de acceso
          </h1>
          <p className="text-sm text-ink-muted">
            Qué ve cada tipo de cuenta, sección por sección. Las cuentas apuntan a un perfil.
          </p>
        </div>
        {canEdit ? (
          <button
            type="button"
            data-help="profiles.new"
            onClick={() => setModal({ kind: 'create' })}
            className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium px-4"
          >
            + Nuevo perfil
          </button>
        ) : null}
      </header>

      <TeamTabs />

      {!canEdit ? (
        <div role="status" className="rounded-lg border border-status-warning/30 bg-status-warning/10 p-3 text-sm text-status-warning">
          Tu perfil tiene solo lectura sobre Equipo. Podés ver los perfiles, no cambiarlos.
        </div>
      ) : null}

      {q.isLoading ? <p className="text-sm text-ink-muted">Cargando…</p> : null}
      {q.isError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          {q.error instanceof AdminApiError ? q.error.message : 'No pudimos cargar los perfiles.'}
        </div>
      ) : null}

      {q.data ? (
        <section
          data-help="profiles.list"
          className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-ink-muted border-b border-surface-border dark:border-surface-dark-border">
                  <th className="text-left px-5 py-2">Perfil</th>
                  {ACCESS_SECTIONS.map((s) => (
                    <th key={s} className="text-center px-2 py-2 whitespace-nowrap font-medium">
                      {SECTION_META[s].label}
                    </th>
                  ))}
                  <th className="text-right px-5 py-2">Cuentas</th>
                  <th className="text-right px-5 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((r) => {
                  const isMine = session?.role === r.role_key;
                  return (
                    <tr
                      key={r.role_key}
                      className="border-b border-surface-border dark:border-surface-dark-border last:border-0 align-top"
                    >
                      <td className="px-5 py-3 min-w-[14rem]">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{r.display_name}</span>
                          {r.is_builtin ? (
                            <span
                              title="Inmutable: siempre ve todo"
                              className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] rounded-full px-2 py-0.5 bg-brand/10 text-brand"
                            >
                              <Lock size={10} aria-hidden="true" /> fijo
                            </span>
                          ) : null}
                          {isMine ? (
                            <span className="whitespace-nowrap text-[11px] rounded-full px-2 py-0.5 bg-status-info/15 text-status-info">
                              tu perfil
                            </span>
                          ) : null}
                        </div>
                        <div className="text-xs font-mono text-ink-muted">{r.role_key}</div>
                        {r.description ? (
                          <p className="mt-1 text-xs text-ink-muted max-w-xs">{r.description}</p>
                        ) : null}
                      </td>
                      {ACCESS_SECTIONS.map((s) => (
                        <td key={s} className="px-2 py-3 text-center">
                          <span
                            className={`inline-block whitespace-nowrap text-[11px] rounded-full px-2 py-0.5 ${levelBadgeCls(r.access[s])}`}
                          >
                            {LEVEL_LABEL[r.access[s]]}
                          </span>
                        </td>
                      ))}
                      <td className="px-5 py-3 text-right tabular-nums">
                        <span className="inline-flex items-center gap-1 text-ink-muted">
                          <Users size={12} aria-hidden="true" /> {r.account_count}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right whitespace-nowrap space-x-2">
                        <button
                          type="button"
                          onClick={() => setModal({ kind: 'edit', target: r })}
                          className="text-sm text-brand hover:underline"
                        >
                          {canEdit ? 'Editar' : 'Ver'}
                        </button>
                        {canEdit ? (
                          <button
                            type="button"
                            onClick={() => setModal({ kind: 'delete', target: r })}
                            disabled={r.is_builtin || r.account_count > 0 || isMine}
                            title={
                              r.is_builtin
                                ? 'El perfil admin no se borra'
                                : r.account_count > 0
                                  ? 'Reasigná primero las cuentas que lo usan'
                                  : isMine
                                    ? 'No podés borrar tu propio perfil'
                                    : ''
                            }
                            className="text-sm text-status-error hover:underline disabled:opacity-30 disabled:no-underline disabled:cursor-not-allowed"
                          >
                            Borrar
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {modal?.kind === 'create' ? (
        <ProfileEditor
          onClose={() => setModal(null)}
          onSaved={() => {
            refresh();
            setModal(null);
          }}
        />
      ) : null}
      {modal?.kind === 'edit' ? (
        <ProfileEditor
          target={modal.target}
          readOnly={!canEdit}
          isMine={session?.role === modal.target.role_key}
          onClose={() => setModal(null)}
          onSaved={() => {
            refresh();
            setModal(null);
          }}
        />
      ) : null}
      {modal?.kind === 'delete' ? (
        <DeleteProfileModal
          target={modal.target}
          onClose={() => setModal(null)}
          onDeleted={() => {
            refresh();
            setModal(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

function emptyAccess(): AccessMap {
  const out = {} as AccessMap;
  for (const s of ACCESS_SECTIONS) out[s] = 'hidden';
  return out;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/^[^a-z]+/, '')
    .slice(0, 40);
}

function ProfileEditor({
  target,
  readOnly = false,
  isMine = false,
  onClose,
  onSaved,
}: {
  target?: AdminRoleItem;
  readOnly?: boolean;
  isMine?: boolean;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const isNew = !target;
  const lockAccess = readOnly || target?.is_builtin === true;

  const [displayName, setDisplayName] = useState(target?.display_name ?? '');
  const [roleKey, setRoleKey] = useState(target?.role_key ?? '');
  const [keyTouched, setKeyTouched] = useState(false);
  const [description, setDescription] = useState(target?.description ?? '');
  const [access, setAccess] = useState<AccessMap>(target?.access ?? emptyAccess());
  const [err, setErr] = useState<string | null>(null);

  const accessChanged = useMemo(
    () => !target || JSON.stringify(access) !== JSON.stringify(target.access),
    [access, target],
  );

  const m = useMutation({
    mutationFn: async () => {
      if (isNew) {
        return createRole({
          role_key: roleKey,
          display_name: displayName.trim(),
          description: description.trim(),
          access,
        });
      }
      const body: Parameters<typeof patchRole>[1] = {};
      if (displayName.trim() !== target.display_name) body.display_name = displayName.trim();
      if (description.trim() !== target.description) body.description = description.trim();
      if (accessChanged && !lockAccess) body.access = access;
      return patchRole(target.role_key, body);
    },
    onSuccess: onSaved,
    onError: (e) => setErr(errorMessage(e, 'No pudimos guardar el perfil.')),
  });

  function setLevel(section: AccessSection, level: AccessLevel): void {
    setAccess((a) => ({ ...a, [section]: level }));
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setErr(null);
    if (isNew && !/^[a-z][a-z0-9_-]{1,39}$/.test(roleKey)) {
      setErr('La clave tiene que ser minúscula, empezar con letra y tener entre 2 y 40 caracteres (letras, números, guiones).');
      return;
    }
    m.mutate();
  }

  const title = isNew
    ? 'Nuevo perfil de acceso'
    : readOnly
      ? `Perfil — ${target.display_name}`
      : `Editar — ${target.display_name}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 bg-ink/50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        data-help="profiles.editor"
        className="bg-surface dark:bg-surface-dark border border-surface-border dark:border-surface-dark-border rounded-xl shadow-lg w-full max-w-3xl mt-8 p-6 space-y-5"
      >
        <header className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-ink dark:hover:text-ink-inverted text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </header>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nombre">
            <input
              type="text"
              required
              maxLength={60}
              disabled={readOnly}
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                if (isNew && !keyTouched) setRoleKey(slugify(e.target.value));
              }}
              className={fieldCls()}
            />
          </Field>
          <Field label="Clave">
            <input
              type="text"
              required
              disabled={!isNew}
              value={roleKey}
              onChange={(e) => {
                setKeyTouched(true);
                setRoleKey(e.target.value);
              }}
              className={fieldCls({ extra: 'font-mono' })}
              placeholder="ej: soporte-nivel-2"
            />
            <p className="mt-1 text-xs text-ink-muted">
              {isNew
                ? 'Identificador interno. No se puede cambiar después.'
                : 'El identificador no se cambia: las cuentas apuntan a él.'}
            </p>
          </Field>
        </div>
        <Field label="Descripción (opcional)">
          <input
            type="text"
            maxLength={400}
            disabled={readOnly}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={fieldCls()}
            placeholder="Para qué es este perfil, en una línea."
          />
        </Field>

        <section data-help="profiles.matrix" className="space-y-2">
          <h3 className="text-sm font-semibold">Qué ve este perfil</h3>
          {target?.is_builtin ? (
            <p className="text-xs text-ink-muted">
              <Lock size={10} className="inline mr-1" aria-hidden="true" />
              El perfil <code className="font-mono">admin</code> ve todo, siempre. Es la puerta por la que se vuelve a arreglar cualquier configuración.
            </p>
          ) : null}
          <div className="rounded-lg border border-surface-border dark:border-surface-dark-border divide-y divide-surface-border dark:divide-surface-dark-border">
            {ACCESS_SECTIONS.map((s) => {
              const meta = SECTION_META[s];
              const level = access[s];
              const options: AccessLevel[] = meta.write ? ['hidden', 'read', 'write'] : ['hidden', 'read'];
              const lockRow = lockAccess || (isMine && s === 'team');
              return (
                <div key={s} className="grid gap-2 p-3 sm:grid-cols-[1fr_auto] sm:items-center">
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                      {meta.label}
                      {meta.pii ? (
                        <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-status-warning/15 text-status-warning">
                          datos personales
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-ink-muted">{meta.summary}</p>
                    <p className="text-xs text-ink-subtle mt-0.5">
                      {level === 'write' && meta.write
                        ? `Completo: ${meta.read} ${meta.write}`
                        : level === 'read'
                          ? `${meta.write ? 'Solo lectura' : 'Visible'}: ${meta.read}`
                          : 'Oculto: no aparece en el menú, ni por URL, ni en el manual.'}
                    </p>
                    {isMine && s === 'team' ? (
                      <p className="text-xs text-status-info mt-0.5">
                        Es tu propio perfil: Equipo queda en completo para que no te quedes afuera.
                      </p>
                    ) : null}
                  </div>
                  <div role="radiogroup" aria-label={`Nivel para ${meta.label}`} className="flex gap-1">
                    {options.map((opt) => {
                      const active = level === opt;
                      return (
                        <label
                          key={opt}
                          className={`cursor-pointer select-none rounded-lg px-3 min-h-[2.25rem] flex items-center text-xs border transition ${
                            active
                              ? `${levelBadgeCls(opt)} border-transparent font-medium`
                              : 'border-surface-border dark:border-surface-dark-border text-ink-muted hover:bg-surface-muted dark:hover:bg-surface-dark-muted'
                          } ${lockRow ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="radio"
                            name={`level-${s}`}
                            value={opt}
                            checked={active}
                            disabled={lockRow}
                            onChange={() => setLevel(s, opt)}
                            className="sr-only"
                          />
                          {opt === 'read' && !meta.write ? 'Visible' : LEVEL_LABEL[opt]}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {!isNew && !lockAccess && accessChanged && target.account_count > 0 ? (
          <div role="status" className="rounded-lg border border-status-warning/30 bg-status-warning/10 p-3 text-sm text-status-warning">
            Guardar cierra la sesión de {target.account_count === 1 ? 'la cuenta' : `las ${target.account_count} cuentas`} con este perfil
            {isMine ? ', incluida la tuya' : ''}. Van a tener que volver a entrar para ver el cambio.
          </div>
        ) : null}

        {err ? (
          <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
            {err}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className={btnSecondary}>
            {readOnly ? 'Cerrar' : 'Cancelar'}
          </button>
          {!readOnly ? (
            <button type="submit" disabled={m.isPending} className={btnPrimary}>
              {m.isPending ? 'Guardando…' : isNew ? 'Crear perfil' : 'Guardar'}
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}

function DeleteProfileModal({
  target,
  onClose,
  onDeleted,
}: {
  target: AdminRoleItem;
  onClose: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => deleteRole(target.role_key),
    onSuccess: onDeleted,
    onError: (e) => setErr(errorMessage(e, 'No pudimos borrar el perfil.')),
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 bg-ink/50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-surface dark:bg-surface-dark border border-surface-border dark:border-surface-dark-border rounded-xl shadow-lg w-full max-w-md mt-12 p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">Borrar — {target.display_name}</h2>
        <p className="text-sm">
          Ninguna cuenta usa este perfil, así que borrarlo no afecta a nadie. No se puede deshacer.
        </p>
        {err ? (
          <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
            {err}
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => {
              setErr(null);
              m.mutate();
            }}
            disabled={m.isPending}
            className="min-h-[2.75rem] rounded-lg bg-status-error hover:opacity-90 text-white font-medium px-4 disabled:opacity-50"
          >
            {m.isPending ? 'Borrando…' : 'Borrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UI helpers (mismos que Admins.tsx)
// ---------------------------------------------------------------------------

const btnPrimary =
  'min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium px-4 disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary =
  'min-h-[2.75rem] rounded-lg border border-surface-border dark:border-surface-dark-border px-4 text-sm hover:bg-surface-muted dark:hover:bg-surface-dark-muted';

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block text-sm space-y-1">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}
